/**
 * OAuth utility functions for Google and Microsoft authentication
 * Implements PKCE (Proof Key for Code Exchange) for secure OAuth flows
 */

/**
 * Generate a random string for PKCE code verifier
 */
const generateRandomString = (length: number): string => {
  const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let text = "";
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }

  return text;
};

/**
 * Generate PKCE code challenge from code verifier
 */
const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  
  // Convert digest to base64url
  const digestArray = new Uint8Array(digest);
  let binaryString = "";
  for (let i = 0; i < digestArray.length; i++) {
    binaryString += String.fromCharCode(digestArray[i]);
  }
  
  const codeChallenge = btoa(binaryString)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

  return codeChallenge;
};

/**
 * Generate Google OAuth URL with PKCE
 */
export const generateGoogleAuthUrl = async (
  isSecondAccount: boolean = false,
  clientId: string,
  redirectUri: string
): Promise<string> => {
  // Generate PKCE code verifier (64 characters as per OAuth guide)
  const codeVerifier = generateRandomString(64);

  // Store code verifier with account-specific key
  const storageKey = isSecondAccount
    ? "pkce_code_verifier_secondary"
    : "pkce_code_verifier_primary";
  sessionStorage.setItem(storageKey, codeVerifier);

  // Generate code challenge
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const stateParam = isSecondAccount ? "&state=secondAccount" : "&state=firstAccount";

  return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/drive.file&prompt=consent&access_type=offline&code_challenge=${codeChallenge}&code_challenge_method=S256${stateParam}`;
};

/**
 * Generate Microsoft OAuth URL with PKCE
 */
export const generateMicrosoftAuthUrl = async (
  isSecondAccount: boolean = false,
  clientId: string,
  redirectUri: string
): Promise<string> => {
  // Generate PKCE code verifier (64 characters as per OAuth guide)
  const codeVerifier = generateRandomString(64);

  // Store code verifier with account-specific key
  const storageKey = isSecondAccount
    ? "pkce_code_verifier_secondary"
    : "pkce_code_verifier_primary";
  sessionStorage.setItem(storageKey, codeVerifier);

  // Generate code challenge
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const stateParam = isSecondAccount ? "&state=secondAccount" : "&state=firstAccount";

  return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_mode=fragment&response_type=code&scope=https://graph.microsoft.com/User.Read https://graph.microsoft.com/Mail.Read https://graph.microsoft.com/Mail.ReadWrite https://graph.microsoft.com/MailboxSettings.Read https://graph.microsoft.com/Calendars.Read https://graph.microsoft.com/Calendars.ReadWrite offline_access&access_type=offline&code_challenge=${codeChallenge}&code_challenge_method=S256${stateParam}`;
};

/**
 * Get stored PKCE code verifier based on account type
 */
export const getStoredCodeVerifier = (isSecondAccount: boolean = false): string | null => {
  const storageKey = isSecondAccount
    ? "pkce_code_verifier_secondary"
    : "pkce_code_verifier_primary";
  const codeVerifier = sessionStorage.getItem(storageKey);

  return codeVerifier;
};

/**
 * Clear stored PKCE code verifier for specific account
 */
export const clearStoredCodeVerifier = (isSecondAccount: boolean = false): void => {
  const storageKey = isSecondAccount
    ? "pkce_code_verifier_secondary"
    : "pkce_code_verifier_primary";
  sessionStorage.removeItem(storageKey);
};

/**
 * Clear all stored PKCE code verifiers
 */
export const clearAllStoredCodeVerifiers = (): void => {
  sessionStorage.removeItem("pkce_code_verifier_primary");
  sessionStorage.removeItem("pkce_code_verifier_secondary");
};

/**
 * Handle OAuth errors
 */
export const handleOAuthError = (error: any): string => {
  if (error.error === "access_denied") {
    return "Access was denied. Please try again and grant the required permissions.";
  }

  if (error.error === "invalid_grant") {
    return "Authentication failed. Please try connecting again.";
  }

  if (error.error === "invalid_request") {
    return "Invalid request. Please check your configuration.";
  }

  return error.error_description || "An unexpected error occurred during authentication.";
};
